import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiClientConfig } from '../api-client-config';
import { DocumentLockResponse } from '@primatoo-ms-organization';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';

import { map } from 'rxjs/operators';
import { ApiClientUtils } from '../../api-client.utils';
import { DeleteEntityServiceInterface } from '../interface/delete-entity-service-interface';
import { CreateResponse } from 'it/primatoo/openapi/api/common/schemas/CreateResponse';
import { UpsertUserColumnRequest } from 'it/primatoo/openapi/api/org/schemas/common/UpsertUserColumnRequest';
import { IncidentTeam } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/IncidentTeam';
import { UpdateIncidentTeamRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/UpdateIncidentTeamRequest';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import { GetDataBreachPermissionResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/GetDataBreachPermissionResponse';
import { UpdateDataBreachPermissionRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/UpdateDataBreachPermissionRequest';
import { ListDataBreachNoteResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/ListDataBreachNoteResponse';
import { CreateNoteRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/CreateNoteRequest';
import { OpenDataBreachRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/OpenDataBreachRequest';
import { GetDataBreachForDetailsResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/GetDataBreachForDetailsResponse';
import { UpdateDataBreachRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/UpdateDataBreachRequest';
import { ListDataBreachHistoryResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/ListDataBreachHistoryResponse';
import { ListDataBreachTreatmentResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/ListDataBreachTreatmentResponse';
import { DataBreach } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/DataBreach';
import { EvaluateDataBreachRiskRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/EvaluateDataBreachRiskRequest';
import { EvaluateDataBreachRiskResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/EvaluateDataBreachRiskResponse';
import { PatchDataBreachRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/patchdatabreachrequest/PatchDataBreachRequest';
import { DataBreachTreatmentSearchRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/DataBreachTreatmentSearchRequest';
import { GetDataBreachAvailableOperationsResponse } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/GetDataBreachAvailableOperationsResponse';
import * as FileSaver from 'file-saver';
import { DownloadFormat, DownloadResponse } from '@pt/pt-models';
import { ListDocumentResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListDocumentResponse';
import { DocumentItem } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem';

@Injectable({
  providedIn: 'root',
})
export class DataBreachProcessService implements DeleteEntityServiceInterface {
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient
  ) {
    this.endpointV2 =
      this.config.endPoint + '/api/org/v2/data-breach-processes/';
  }

  public reindex(): Observable<void> {
    const url = this.endpointV2 + `reindex`;
    return this.httpClient.post<void>(url, {});
  }

  public findById(id: number): Observable<DataBreach> {
    const url = this.endpointV2 + `${id}/`;
    return this.httpClient.get<DataBreach>(url);
  }

  // ?sort=level,DESC&sort=id,DESC
  public findAllFiltered(
    filter?: string,
    filterByAnyPermission = false,
    columns?: string[]
  ): Observable<Array<DocumentItem>> {
    let url = this.endpointV2; // '?sort=level,DESC&sort=id,DESC' + (filter ? ('&q=' + filter) : '');
    url += filter ? filter : '';
    url += '?sort=level,DESC';
    url += '&sort=id,DESC';
    url += filterByAnyPermission
      ? '&filterByAnyPermission=true'
      : '&filterByAnyPermission=false';
    if (columns && columns.length > 0) {
      url += '&columns=' + columns.join('%7C'); //columns.join('|');
    }

    return this.httpClient
      .get<ListDocumentResponse>(url)
      .pipe(map((response) => response?.documents ?? []));
  }

  public getActivePermission(
    id: number
  ): Observable<GetDataBreachPermissionResponse> {
    const url = this.endpointV2 + `${id}/permissions/`;
    return this.httpClient.get<GetDataBreachPermissionResponse>(url);
  }

  public patchActivePermission(
    id: number,
    request: UpdateDataBreachPermissionRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${id}/permissions/`;
    return this.httpClient.patch<void>(url, request);
  }

  private downloadPdfUrl(id: number, type: 'pdf' | 'html'): string {
    return this.endpointV2 + `${id}/${type}`;
  }

  public downloadArchivedPdf(
    resourceId: number,
    fileName?: string
  ): Observable<DownloadResponse> {
    const url = this.downloadPdfUrl(resourceId, 'pdf');
    return ApiClientUtils.download(
      this.httpClient,
      url,
      DownloadFormat.PDF,
      fileName
    );
  }

  public downloadPdf(id: number) {
    ApiClientUtils.downloadComplete(
      this.httpClient,
      this.downloadPdfUrl(id, 'pdf'),
      DownloadFormat.PDF
    );
  }

  public downloadHtml(id: number, name: string) {
    const url = this.downloadPdfUrl(id, 'html');
    this.httpClient.get(url).subscribe((s: any) => {
      FileSaver.saveAs(new Blob([s.body]), name + '.html');
    });
  }

  public getNotes(id: number): Observable<ListDataBreachNoteResponse> {
    const url = this.endpointV2 + `${id}/notes/`;
    return this.httpClient.get<ListDataBreachNoteResponse>(url);
  }

  public updateDataBreach(
    id: number,
    request: UpdateDataBreachRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${id}/`;
    return this.httpClient.put<void>(url, request);
  }

  public patchDataBreach(
    id: number,
    request: PatchDataBreachRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${id}/`;
    return this.httpClient.patch<void>(url, request);
  }

  public createNotes(
    id: number,
    request: CreateNoteRequest
  ): Observable<CreateResponse> {
    const url = this.endpointV2 + `${id}/notes/`;
    return this.httpClient.post<CreateResponse>(url, request);
  }

  public deleteNote(id: number, noteId: number): Observable<void> {
    const url = this.endpointV2 + `${id}/notes/${noteId}/`;
    return this.httpClient.delete<void>(url);
  }

  public getDetails(id: number): Observable<GetDataBreachForDetailsResponse> {
    const url = this.endpointV2 + `${id}/details/`;
    return this.httpClient.get<GetDataBreachForDetailsResponse>(url);
  }

  public setUserColumns(
    columnsConfigurationUpsertRequest: UpsertUserColumnRequest
  ): Observable<void> {
    const url = this.endpointV2 + 'user-columns/';
    return this.httpClient.post<void>(url, columnsConfigurationUpsertRequest);
  }

  public update(
    id: number,
    request: UpdateDataBreachRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${id}/`;
    return this.httpClient.post<void>(url, request);
  }

  public delete(id: number): Observable<void> {
    const url = this.endpointV2 + `${id}/`;
    return this.httpClient.delete<void>(url);
  }

  public close(id: number): Observable<void> {
    const url = this.endpointV2 + `${id}/close`;
    return this.httpClient.post<void>(url, {});
  }

  public openDataBreach(
    id: number,
    request: OpenDataBreachRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${id}/open`;
    return this.httpClient.post<void>(url, request);
  }

  public cancelDeleting(id: number): Observable<void> {
    const url = this.endpointV2 + `${id}/cancel-deleting`;
    return this.httpClient.post<void>(url, {});
  }

  public cancelArchiving(id: number): Observable<void> {
    const url = this.endpointV2 + `${id}/cancel-archiving`;
    return this.httpClient.post<void>(url, {});
  }

  public archive(id: number): Observable<void> {
    const url = this.endpointV2 + `${id}/archive`;
    return this.httpClient.post<void>(url, {});
  }

  public getIncidentTeamById(id: number): Observable<IncidentTeam> {
    const url = this.endpointV2 + `${id}/incident-team/`;
    return this.httpClient.get<IncidentTeam>(url);
  }

  public updateDataBreachIncidentTeamById(
    id: number,
    request: UpdateIncidentTeamRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${id}/incident-team/`;
    return this.httpClient.put<void>(url, request);
  }

  public getIncidentTeam(): Observable<IncidentTeam> {
    const url = this.endpointV2 + `incident-team/`;
    return this.httpClient.get<IncidentTeam>(url);
  }

  public updateDataBreachIncidentTeam(
    request: UpdateIncidentTeamRequest
  ): Observable<void> {
    const url = this.endpointV2 + `incident-team/`;
    return this.httpClient.put<void>(url, request);
  }

  public getHistory(id: number): Observable<ListDataBreachHistoryResponse> {
    const url = this.endpointV2 + `${id}/history/`;
    return this.httpClient.get<ListDataBreachHistoryResponse>(url);
  }

  public getColsEnabled(): Observable<ColumnWithLocalization[]> {
    const url = this.endpointV2 + `columns/`;
    return this.httpClient
      .get<ListUserColumnsResponse>(url)
      .pipe(map((a) => a.columns));
  }

  public calcRiskEvaluation(
    request: EvaluateDataBreachRiskRequest
  ): Observable<EvaluateDataBreachRiskResponse> {
    const url = this.endpointV2 + `risk-evaluation`;
    return this.httpClient.post<EvaluateDataBreachRiskResponse>(url, request);
  }

  // @Mock({response: {kind: 'jsonFile', fileName: 'ListDataBreachTreatmentResponse'}})
  public treatmentsSearch(
    request: DataBreachTreatmentSearchRequest
  ): Observable<ListDataBreachTreatmentResponse> {
    const url = this.endpointV2 + `treatments/`;
    return this.httpClient.post<ListDataBreachTreatmentResponse>(url, request);
  }

  public findAllIncidentTeam(): Observable<IncidentTeam> {
    const url = this.endpointV2 + `incident-team/`;
    return this.httpClient.get<IncidentTeam>(url);
  }

  public updateIncidentTeam(
    request: UpdateIncidentTeamRequest
  ): Observable<void> {
    const url = this.endpointV2 + `incident-team/`;
    return this.httpClient.put<void>(url, request);
  }

  deleteMultiple(ids: number[]): Observable<{ failed: number[] }> {
    return undefined;
  }

  getDocDependencies(id: number): Observable<any> {
    return undefined;
  }

  lockResource(id: number, token: number): Observable<DocumentLockResponse> {
    let url = this.endpointV2 + `${id}/lock`;
    if (token !== null) {
      url = `${url}?token=${token}`;
    }
    return this.httpClient.post<DocumentLockResponse>(url, {});
  }

  releaseLock(id: number): Observable<void> {
    const url = this.endpointV2 + `${id}/lock`;
    return this.httpClient.delete<void>(url, {});
  }
  getDocUserPermission(
    docId: number
  ): Observable<GetDataBreachAvailableOperationsResponse> {
    const url = this.endpointV2 + `${docId}/user-operations/`;
    return this.httpClient.get<GetDataBreachAvailableOperationsResponse>(url);
  }

  canClose(
    databreach: UpdateDataBreachRequest
  ): Observable<{ closable: boolean }> {
    const url = this.endpointV2 + `can-close`;
    return this.httpClient.post<{ closable: boolean }>(url, databreach);
  }
}
